import { createMachine, interpret } from '@xstate/fsm';
import StateObserver, { now, user } from 'src/StateObserver';
import { TutorialEvent } from 'src/game/tutorial/TutorialController';
import OrdinaryTutorial from 'src/game/tutorial/handlers/OrdinaryTutorial';
import { TutorialServices } from 'src/game/tutorial/services/TutorialServices';
import { setupTutorialFSM } from 'src/game/tutorial/tutorial.util';
import * as Tutorial from 'src/replicant/tutorial';
import { TutorialStep } from 'src/replicant/tutorial/tutorial.ruleset';
import {
  puzzleCoinsTrackStepFinish,
  puzzleCoinsTrackStepStart,
  puzzleCoinsTrackTutorialFinish,
} from '../puzzlecoins.tracking';
import { getPet } from 'src/game/pet';
import { isFirstEntry } from 'src/lib/platformUtils';
import { puzzleCoinsIsPlayingTutorialDeterministic } from 'src/replicant/puzzlecoins/puzzlecoins.getters';
import { closeAllPopups } from 'src/lib/popups/popupOpenClose';

const events: TutorialEvent[] = [
  'gameStarted',
  'careMenuOpened',
  'carePopupOpened',
  'careConfirmPopupOpened',
  'carePopupClosed',
  'puzzleCoinsBasicStepComplete',
  'careItemConfirmed',
];

export class PuzzleCoinsCareTutorial extends OrdinaryTutorial {
  public readonly step: TutorialStep = 'puzzleCoinsCare';
  private foodMachine = setupTutorialFSM(events, () =>
    foodTutorialFSM(this.services),
  );
  private toyMachine = setupTutorialFSM(events, () =>
    toyTutorialFSM(this.services),
  );

  public async handleEvent(event: TutorialEvent): Promise<boolean> {
    if (!Tutorial.getters.isTutorialStepComplete(user(), 'puzzleCoinsBasic')) {
      return false;
    }

    if (Tutorial.getters.isTutorialStepComplete(user(), 'puzzleCoinsCare')) {
      return false;
    }

    if (!events.includes(event)) {
      return false;
    }

    // Show the "I git better" dialog if playing determinist tutorial and it's not the first entry
    if (
      event === 'gameStarted' &&
      !isFirstEntry() &&
      puzzleCoinsIsPlayingTutorialDeterministic(user())
    ) {
      await getPet().conversation.playConversation({
        dialogScene: 'tutorial_recover',
        retry: true,
      });
    }

    if (user().care.careActionCount === 0) {
      await this.foodMachine.handleEvent(event);
    } else {
      await this.toyMachine.handleEvent(event);
    }

    return true;
  }
}

function completeTutorialStep() {
  if (Tutorial.getters.isTutorialStepComplete(user(), 'puzzleCoinsCare')) {
    return;
  }
  StateObserver.invoke.completeTutorialStep('puzzleCoinsCare');
}

function foodTutorialFSM(services: TutorialServices) {
  const machine = interpret(
    createMachine(
      {
        context: {},
        id: 'Care',
        initial: 'openCareMenu',
        states: {
          openCareMenu: {
            entry: () => {
              puzzleCoinsTrackStepStart('foodCare');
              void services.hand.show({ tag: 'careButton', x: 60, y: 50 });
            },
            on: {
              careMenuOpened: 'selectCareItem',
            },
          },
          selectCareItem: {
            entry: () => {
              void services.hand.show({
                tag: 'foodCareMenuButton',
                x: 60,
                y: 50,
              });
            },
            on: {
              carePopupOpened: 'selectFoodItem',
            },
          },
          selectFoodItem: {
            entry: () => {
              void services.hand.show({
                tag: 'lastCareItem',
                x: 60,
                y: 50,
                zIndex: 99999,
              });
            },
            on: {
              careItemConfirmed: 'completeTutorial',
            },
          },
          completeTutorial: {
            entry: () => {
              puzzleCoinsTrackStepFinish('foodCare');
              machine.send({ type: 'goToFinal' });
            },
            on: {
              goToFinal: 'final',
            },
          },
          final: {},
        },
      },
      {
        actions: {},
      },
    ),
  );

  return machine;
}

function toyTutorialFSM(services: TutorialServices) {
  const machine = interpret(
    createMachine(
      {
        context: {},
        id: 'Care',
        initial: 'openCareMenu',
        states: {
          openCareMenu: {
            entry: () => {
              puzzleCoinsTrackStepStart('toyCare');
              void services.hand.show({ tag: 'careButton', x: 60, y: 50 });
            },
            on: {
              careMenuOpened: 'selectCareItem',
            },
          },
          selectCareItem: {
            entry: () => {
              void services.hand.show({
                tag: 'toyCareMenuButton',
                x: 60,
                y: 50,
              });
            },
            on: {
              carePopupOpened: 'selectToyItem',
            },
          },
          selectToyItem: {
            entry: () => {
              void services.hand.show({
                tag: 'lastCareItem',
                x: 60,
                y: 50,
                zIndex: 99999,
              });
            },
            on: {
              careItemConfirmed: 'completeTutorial',
            },
          },
          completeTutorial: {
            entry: () => {
              completeTutorialStep();
              puzzleCoinsTrackStepFinish('toyCare');
              puzzleCoinsTrackTutorialFinish();
              machine.send({ type: 'goToFinal' });
            },
            on: {
              goToFinal: 'final',
            },
          },
          final: {},
        },
      },
      {
        actions: {},
      },
    ),
  );

  return machine;
}
